import React from "react";
import { ConfigProvider } from "antd";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { ProtectedRoute } from "./routes/ProtectedRoute";
import { Login } from "./pages/Login/Login";
import { ControlPanel } from "./pages/ControlPanel/ControlPanel";
import { AddFile } from "./pages/AddFile/AddFile";
import { ReportsList } from "./pages/ReportsList/ReportsList";
import { MyFunds } from "./pages/MyFunds/MyFunds";
import { UserProvider } from "./store/UserProvider";
import { TenantProvider } from "./store/TenantProvider";
import { AppProvider } from "./store/AppProvider";
import { ToastProvider } from "./store/ToastProvider";
import { UploadedFilesProvider } from "./store/UploadedFileProvider";
import { GlobalSearchProvider } from "./store/GlobalSearchProvider";
import { Screener } from "./pages/Screener/Screener";
import { CompanyProfile } from "./pages/CompanyProfile/CompanyProfile";
import { StewardshipHomepage } from "./pages/StewardshipHomepage/StewardshipHomepage";
import { ROUTES } from "./routes/routeNames";
import { Workspace } from "./pages/Workspace/Workspace";
import { CompanyEngagements } from "./pages/CompanyEngagements/CompanyEngagements";
import { SingleEngagement } from "./pages/SingleEngagement/SingleEngagement";
import { ANTD_THEME } from "./styles";
import { MagicUpload } from "./pages/MagicUpload/MagicUpload";
import { CompassAI } from "./pages/CompassAI/CompassAI";
import { LinkageDashboard } from "./pages/LinkageDashboard/LinkageDashboard";
import { AssessmentInsights } from "./pages/AssessmentInsights";
import { AssessmentView } from "./pages/SingleEngagement/AssessmentView/AssessmentView";
import { useJobsPoller } from "./hooks/useJobsPoller";
import { useApp } from "./store/AppProvider";

const JobsSyncManager: React.FC = () => {
  const { app } = useApp();
  if (!app.authorisation.token) return null;
  return <JobsSyncRunner />;
};

const JobsSyncRunner: React.FC = () => {
  useJobsPoller();
  return null;
};

const App: React.FC = () => {
  return (
    <UserProvider>
      <TenantProvider>
        <AppProvider>
          <ToastProvider>
            <JobsSyncManager />
            <UploadedFilesProvider>
              <ConfigProvider theme={ANTD_THEME}>
                <BrowserRouter>
                  <GlobalSearchProvider>
                    <Routes>
                      <Route path={ROUTES.LOGIN} element={<Login />} />
                      <Route
                        path={ROUTES.MAGIC_UPLOAD}
                        element={<MagicUpload />}
                      />

                      {/* All protected routes are nested within this Route */}
                      <Route element={<ProtectedRoute />}>
                        <Route
                          path={ROUTES.CONTROL_PANEL}
                          element={<ControlPanel />}
                        />
                        {/* <Route
                  path="/another-protected-route"
                  element={<AnotherProtectedComponent />}
                /> */}
                        <Route
                          path={ROUTES.ADD_FILE_DEFINITION}
                          element={<AddFile />}
                        />
                        <Route
                          path={ROUTES.REPORTS_LIST}
                          element={<ReportsList />}
                        />
                        <Route path={ROUTES.MY_FUNDS} element={<MyFunds />} />

                        <Route
                          path={ROUTES.SCREENING_TOOL_BASE_DEFINITION}
                          element={<Screener />}
                        />
                        <Route
                          path={ROUTES.COMPANY_PROFILE_DEFINITION}
                          element={<CompanyProfile />}
                        />
                        <Route
                          path={ROUTES.STEWARDSHIP}
                          element={<StewardshipHomepage />}
                        />
                        {/* Company Engagements - list view for all engagements of a company */}
                        <Route
                          path={ROUTES.COMPANY_ENGAGEMENTS_DEFINITION}
                          element={<CompanyEngagements />}
                        />
                        {/* Single Engagement - detail view for one engagement */}
                        <Route
                          path={ROUTES.SINGLE_ENGAGEMENT_DEFINITION}
                          element={<SingleEngagement />}
                        />
                        {/* Legacy routes - backward compatibility */}
                        <Route
                          path={ROUTES.ENGAGEMENT_MODULE_DEFINITION}
                          element={<CompanyEngagements />}
                        />
                        <Route
                          path={ROUTES.ENGAGEMENT_DETAILS_DEFINITION}
                          element={<SingleEngagement />}
                        />

                        <Route
                          path={ROUTES.WORKSPACE_DEFINITION}
                          element={<Workspace />}
                        />
                        <Route
                          path={ROUTES.COMPASS_AI}
                          element={<CompassAI />}
                        />
                        <Route
                          path={ROUTES.LINKAGE_DASHBOARD}
                          element={<LinkageDashboard />}
                        />
                        <Route
                          path={ROUTES.ASSESSMENT_INSIGHTS}
                          element={<AssessmentInsights />}
                        />
                        <Route
                          path={ROUTES.ASSESSMENT_VIEW_DEFINITION}
                          element={<AssessmentView />}
                        />
                        {/* More protected routes can be added here */}
                      </Route>

                      {/* Redirect to a default route if none specified */}
                      <Route
                        path="*"
                        element={<Navigate to={ROUTES.CONTROL_PANEL} replace />}
                      />
                    </Routes>
                  </GlobalSearchProvider>
                </BrowserRouter>
              </ConfigProvider>
            </UploadedFilesProvider>
          </ToastProvider>
        </AppProvider>
      </TenantProvider>
    </UserProvider>
  );
};

export default App;
